import { Component, ComponentType } from "@/types";
import { BaseCommandHandler, CommandResult, PropertyChange } from "../BaseCommandHandler";
import { createThemePatterns, createColorPatterns } from "./ControlPatternMixins";

const VALID_THEMES = ['ios', 'material', 'pill', 'neon', 'minimal'] as const;

const THEME_ALIASES: Record<string, string> = {
  'apple': 'ios',
  'google': 'material',
  'android': 'material',
  'capsule': 'pill',
  'glow': 'neon',
  'simple': 'minimal',
  'clean': 'minimal',
  'text': 'minimal',
};

interface Segment {
  id: string;
  label: string;
  icon?: string;
}

export class SegmentedControlCommandHandler extends BaseCommandHandler {
  constructor() {
    super();
    this.initializePatterns();
  }

  private initializePatterns(): void {
    // Shared mixin patterns
    for (const [regex, handler] of createThemePatterns([...VALID_THEMES], THEME_ALIASES)) {
      this.commandPatterns.set(regex, handler);
    }
    for (const [regex, handler] of createColorPatterns(['selectedColor', 'unselectedColor', 'textColor', 'selectedTextColor'])) {
      this.commandPatterns.set(regex, handler);
    }

    // ─── Custom Segment Management Patterns ──────────────────────────────

    // "set options X Y Z" or "set segments X Y Z"
    this.commandPatterns.set(
      /(?:set )?(?:options?|segments?|items?|choices?) (?:to )?(.+)/i,
      (match) => {
        const labels = this.parseSegmentLabels(match[1]);
        if (labels.length < 2) return null;
        return {
          properties: {
            segments: labels.map((label, i) => ({ id: `seg-${i}`, label: label.trim() })),
            selectedIndex: 0,
          },
        };
      }
    );

    // "select X" or "choose X"
    this.commandPatterns.set(
      /(?:select|choose|pick|activate) (.+)/i,
      (match, component) => {
        const targetLabel = match[1].trim().toLowerCase();
        const segments = (component.properties?.segments as Segment[]) || [];
        const index = segments.findIndex(
          (s) => s.label.toLowerCase() === targetLabel
        );
        if (index === -1) return null;
        return { properties: { selectedIndex: index } };
      }
    );

    // "add option X"
    this.commandPatterns.set(
      /add (?:option|segment|item|choice) (.+)/i,
      (match, component) => {
        const segments = [...((component.properties?.segments as Segment[]) || [])];
        segments.push({ id: `seg-${Date.now()}`, label: match[1].trim() });
        return { properties: { segments } };
      }
    );

    // "remove option X"
    this.commandPatterns.set(
      /(?:remove|delete) (?:option|segment|item|choice) (.+)/i,
      (match, component) => {
        const targetLabel = match[1].trim().toLowerCase();
        const segments = ((component.properties?.segments as Segment[]) || []).filter(
          (s) => s.label.toLowerCase() !== targetLabel
        );
        if (segments.length < 2) return null;
        const selectedIndex = Math.min(
          (component.properties?.selectedIndex as number) || 0,
          segments.length - 1
        );
        return { properties: { segments, selectedIndex } };
      }
    );

    // "orientation horizontal/vertical"
    this.commandPatterns.set(
      /(?:set |make |use )?(?:orientation )?(?:to )?(horizontal|vertical)/i,
      (match) => ({ properties: { orientation: match[1].toLowerCase() } })
    );

    // "size sm/md/lg"
    this.commandPatterns.set(
      /(?:set |make )?size (?:to )?(sm|small|md|medium|lg|large)/i,
      (match) => {
        const sizeMap: Record<string, string> = {
          small: 'sm', sm: 'sm', medium: 'md', md: 'md', large: 'lg', lg: 'lg',
        };
        return { properties: { size: sizeMap[match[1].toLowerCase()] || 'md' } };
      }
    );

    // "equal width on/off"
    this.commandPatterns.set(
      /(?:set |make )?equal(?:\s+width)?(?:\s+(on|off|true|false))?/i,
      (match) => {
        const off = match[1] && /^(off|false)$/i.test(match[1]);
        return { properties: { equalWidth: !off } };
      }
    );
  }

  private parseSegmentLabels(input: string): string[] {
    if (input.includes(',')) {
      return input.split(',').map(s => s.trim()).filter(Boolean);
    }
    if (/\band\b/i.test(input)) {
      return input.split(/\s+and\s+/i).map(s => s.trim()).filter(Boolean);
    }
    return input.split(/\s+/).map(s => s.trim()).filter(Boolean);
  }

  canHandle(command: string, component: Component): boolean {
    if (component.type !== ComponentType.SEGMENTED_CONTROL) return false;
    const keywords = [
      'segment', 'option', 'select', 'choose', 'tab', 'theme',
      'color', 'horizontal', 'vertical', 'equal', 'size', 'add', 'remove',
      'delete', 'pill', 'ios', 'material', 'neon', 'minimal', 'item', 'choice',
      'pick', 'activate', 'orientation',
    ];
    const lc = command.toLowerCase();
    return keywords.some(k => lc.includes(k));
  }

  async parseCommand(command: string, component: Component): Promise<CommandResult> {
    const commandParts = command.split(/\s+(?:and|with|also)\s+/i);
    const changes: PropertyChange = { properties: {} };

    for (const part of commandParts) {
      const trimmed = part.trim();
      if (!trimmed) continue;
      const patternMatch = this.matchPatterns(trimmed, component);
      if (patternMatch?.properties) {
        changes.properties = { ...changes.properties, ...patternMatch.properties };
      }
    }

    if (!changes.properties || Object.keys(changes.properties).length === 0) {
      return {
        success: false,
        error: 'Could not understand segmented control command.',
        suggestions: await this.getExampleCommands(ComponentType.SEGMENTED_CONTROL),
      };
    }

    const validation = this.validateChanges(changes, component);
    if (!validation.valid) {
      return { success: false, error: validation.error };
    }

    return {
      success: true,
      command: {
        type: this.determineCommandType(changes),
        changes,
        confidence: this.calculateConfidence(command, changes),
        description: this.generateDescription(changes, component),
      },
    };
  }

  async getExampleCommands(_componentType: ComponentType): Promise<string[]> {
    return [
      'set options Auto Cool Heat Fan',
      'select Heat',
      'add option Dry',
      'remove option Fan',
      'theme ios',
      'theme pill',
      'make vertical',
      'size large',
      'equal width',
      'selected color #007AFF',
    ];
  }

  async getSuggestions(component: Component): Promise<string[]> {
    const suggestions: string[] = [];
    const props = (component.properties || {}) as Record<string, unknown>;
    const segments = (props.segments as Segment[]) || [];

    if (segments.length === 0) {
      suggestions.push('set options Option1 Option2 Option3');
    } else {
      suggestions.push('add option NewOption');
      if (segments.length > 2) {
        suggestions.push(`remove option ${segments[segments.length - 1]?.label || 'last'}`);
      }
    }
    suggestions.push(`theme ${props.theme === 'ios' ? 'pill' : 'ios'}`);
    if (props.orientation !== 'vertical') {
      suggestions.push('make vertical');
    }
    return suggestions.slice(0, 6);
  }
}

export const segmentedControlCommandHandler = new SegmentedControlCommandHandler();
